import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from '@kostbar/i18n'
import { onHandQty } from '../lib/stock'
import { inventoryApi } from '@/api/endpoints/inventory'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { formatStockDisplay } from '@/shared/lib/stock-quantity'

interface SellableHintProps {
  sku: string
  restaurantId: number
  sellableQty: number
  /**
   * Overrides the incoming quantity the tooltip would otherwise read from the levels endpoint.
   *
   * Only the menu planner passes it, and it has to: the levels endpoint answers for TODAY, while
   * a menu is planned against its own day (CHG-2026-09-menu-editor-redesign). Without the
   * override a menu for tomorrow would show tomorrow's figure in the row and today's in the
   * tooltip — and on a narrow window the tooltip is the only way to the four terms.
   */
  incomingQty?: number
  /**
   * Render the bare number, without the "Verkäuflich:" prefix — for the menu planner's ledger,
   * where a legend above the section already names the column. The tooltip stays.
   */
  valueOnly?: boolean
  className?: string
}

/**
 * The sellable figure with its meaning one hover away: the full stock breakdown
 * (fetched fresh when the tooltip opens, like the stock dialog) and the formula.
 * "Verkäuflich" is deliberately the same word as the inventory table column.
 */
export function SellableHint({
  sku,
  restaurantId,
  sellableQty,
  incomingQty,
  valueOnly = false,
  className,
}: SellableHintProps) {
  const { t: tInventory } = useTranslation('inventory')
  const [open, setOpen] = useState(false)

  const { data: levels } = useQuery({
    queryKey: ['inventory', sku, restaurantId],
    queryFn: () => inventoryApi.getOne(sku, restaurantId),
    enabled: open,
  })

  // Null quantities mean "no inventory row yet"; '…' means the fetch is still running.
  const qty = (n: number | null | undefined) =>
    levels ? formatStockDisplay(n ?? 0) : '…'

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <p className={cn('cursor-help', className)}>
          {valueOnly
            ? formatStockDisplay(sellableQty)
            : `${tInventory('table.sellable')}: ${formatStockDisplay(sellableQty)}`}
        </p>
      </TooltipTrigger>
      <TooltipContent className="max-w-64 space-y-1">
        <p>
          {`${tInventory('table.onHand')}: ${
            levels
              ? formatStockDisplay(
                  onHandQty(levels.availableQty, levels.reservedQty),
                )
              : '…'
          } · ${tInventory('table.reserved')}: ${qty(levels?.reservedQty)} · ${tInventory('table.incoming')}: ${incomingQty ?? qty(levels?.incomingQty)} · ${tInventory('table.safetyBuffer')}: ${qty(levels?.safetyBufferQty)}`}
        </p>
        <p className="font-medium">{tInventory('sellableHint.formula')}</p>
        <p>{tInventory('sellableHint.explanation')}</p>
        <p>{tInventory('sellableHint.incomingExplanation')}</p>
      </TooltipContent>
    </Tooltip>
  )
}
